/**
 * Datenbank-Umgebungen — gemeinsame Wahrheit für Server und UI.
 *
 * Drei Umgebungen, baugleich: Cloud SQL Connector mit IAM-Auth, dazu je ein
 * eigener Service Account für MySQL, BigQuery und Cloud Tasks. Jede hat ihren
 * eigenen Screener unter eigener Adresse (`appUrl`) — die Menüleiste wechselt
 * zwischen ihnen per Link, nicht per Datenbank-Umschaltung (23.09.2026).
 *
 * Dazu kommt das technische Profil `prod`: die Verbindung aus CLOUD_* / DB_*
 * der .env, mit der der Server startet (config/database.ts). Es ist KEINE
 * Umgebung mehr — die Altplattform (orbit-prd) ist am 23.09.2026 aus der
 * Oberfläche ausgebaut — bleibt aber als Rückfall bestehen: lokal ohne
 * Prefix-Konfiguration, und auf einem deployten Dienst zeigt es auf dieselbe
 * Instanz wie das Profil der Umgebung. Maschinen-Aufrufer (n8n-Callbacks,
 * Cloud Tasks) dürfen es weiterhin als `db_profile` schicken: ein Lauf, der
 * unter `prod` gestartet wurde, meldet sich mit `prod` zurück.
 */

export type DbProfile = 'prod' | 'prd_v2' | 'sta' | 'int';

/** Die Profile, hinter denen eine echte Umgebung mit eigenem Screener steht. */
export type DbEnvironmentProfile = Exclude<DbProfile, 'prod'>;

export interface DbEnvironmentMeta {
  profile: DbEnvironmentProfile;
  /** Anzeigename in der Oberfläche. */
  label: string;
  /** Kurzhinweis unter dem Schalter. */
  hint: string;
  /** Farbe des aktiven Schalters. */
  color: string;
  /**
   * GCP-Projekt der Umgebung — MySQL, BigQuery, Cloud Tasks und Cloud Scheduler
   * liegen alle dort. Das ist KONFIGURATION, nicht Anzeige:
   * `config/dbEnvironments.ts` leitet daraus das Ziel-Projekt ab, wenn die .env
   * für diese Umgebung nichts Eigenes sagt.
   */
  gcpProject: string;
  /**
   * Adresse des Screeners dieser Umgebung — das Ziel des Umgebungswechsels in
   * der Menüleiste. int und sta laufen unter ihrem Präfix, prd unter der
   * Hauptadresse (Annahme 23.09.2026: prd hat noch keinen Screener; bekommt er
   * einen anderen Host, ist das genau diese eine Zeile).
   */
  appUrl: string;
}

/** Reihenfolge = Reihenfolge in der Oberfläche (der Weg des Codes: int → sta → prod). */
export const DB_ENVIRONMENTS: DbEnvironmentMeta[] = [
  { profile: 'int',    label: 'INT',  hint: 'diga-int', color: '#f59e0b', gcpProject: 'diga-int', appUrl: 'https://int.screener.ambition-performance.de' },
  { profile: 'sta',    label: 'Sta',  hint: 'diga-sta', color: '#3b82f6', gcpProject: 'diga-sta', appUrl: 'https://sta.screener.ambition-performance.de' },
  { profile: 'prd_v2', label: 'Prod', hint: 'diga-prd', color: '#22c55e', gcpProject: 'diga-prd', appUrl: 'https://screener.ambition-performance.de' },
];

/** Das technische Standardprofil (siehe Kopf der Datei). */
export const DEFAULT_DB_PROFILE: DbProfile = 'prod';

/** Alle gültigen Profile: der technische Standard plus die drei Umgebungen. */
export const DB_PROFILES: DbProfile[] = [DEFAULT_DB_PROFILE, ...DB_ENVIRONMENTS.map((e) => e.profile)];

export function isDbProfile(value: unknown): value is DbProfile {
  return typeof value === 'string' && (DB_PROFILES as string[]).includes(value);
}

export function dbEnvironmentMeta(profile: DbProfile): DbEnvironmentMeta | null {
  return DB_ENVIRONMENTS.find((e) => e.profile === profile) ?? null;
}

/** GCP-Projekt einer Umgebung. `null` für das Standardprofil, das kein eigenes Projekt hat. */
export function gcpProjectFor(profile: DbProfile): string | null {
  return dbEnvironmentMeta(profile)?.gcpProject ?? null;
}

/** Umgebung zu einem GCP-Projekt (diga-sta → sta); `null` für fremde Projekte. */
export function profileForGcpProject(project: string): DbEnvironmentProfile | null {
  const wanted = project.trim().toLowerCase();
  return DB_ENVIRONMENTS.find((e) => e.gcpProject === wanted)?.profile ?? null;
}

/** Anzeigename; das Standardprofil heißt in der Oberfläche „Standard". */
export function dbEnvironmentLabel(profile: DbProfile): string {
  return dbEnvironmentMeta(profile)?.label ?? 'Standard';
}

/**
 * Welche Umgebung hinter einem Hostnamen steckt (`window.location.hostname`).
 * `null` = keine: lokale Entwicklung oder ein unbekannter Host.
 */
export function environmentForHostname(hostname: string): DbEnvironmentMeta | null {
  const wanted = hostname.trim().toLowerCase();
  if (!wanted) return null;
  return DB_ENVIRONMENTS.find((e) => new URL(e.appUrl).hostname === wanted) ?? null;
}
